import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  Tag,
  Select,
  DatePicker,
  Input,
  Button,
  Space,
  Typography,
  Card,
  Statistic,
  Row,
  Col,
  Tooltip,
  Drawer,
  Modal,
  InputNumber,
  Radio,
  message,
} from "antd";
import {
  SearchOutlined,
  ReloadOutlined,
  InfoCircleOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { useAuth } from "../../utils/useAuth";
import { isSuperAdmin } from "../../utils/permissions";

import {
  getActivityLogs,
  getActivityStats,
  previewActivityCleanup,
  cleanupActivityLogs,
  type ActivityLog,
  type ActivityAction,
  type ActivityModule,
  type GetActivityLogsParams,
} from "../../utils/api/activityTrailAPI";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const ACTION_COLORS: Record<ActivityAction, string> = {
  CREATE: "green",
  UPDATE: "blue",
  DELETE: "red",
  LOGIN: "purple",
};

const ACTION_LABELS: Record<ActivityAction, string> = {
  CREATE: "Kreiranje",
  UPDATE: "Izmena",
  DELETE: "Brisanje",
  LOGIN: "Prijava",
};

const MODULE_LABELS: Record<ActivityModule, string> = {
  dailyRevenue: "Dnevni prihod",
  userProfile: "Profil korisnika",
  finance: "Finansije",
  apartmentReservation: "Rezervacija apartmana",
  tableReservation: "Rezervacija stola",
  shifts: "Smene",
  credit: "Kredit/Zajam",
  auth: "Prijava",
  hr: "HR / Zaposleni",
  accessControl: "Pristupni profili",
  supplier: "Dobavljači / Rashodi",
  investment: "Investicije",
  documents: "Dokumenti",
  settings: "Podešavanja",
  subscription: "Pretplata",
  support: "Podrška",
  patients: "Pacijenti",
  venueEvent: "Eventi",
};

const MODULE_OPTIONS: { value: ActivityModule; label: string }[] = [
  { value: "auth", label: "Prijava" },
  { value: "dailyRevenue", label: "Dnevni prihod" },
  { value: "userProfile", label: "Profil korisnika" },
  { value: "finance", label: "Finansije" },
  { value: "apartmentReservation", label: "Rezervacija apartmana" },
  { value: "tableReservation", label: "Rezervacija stola" },
  { value: "shifts", label: "Smene" },
  { value: "credit", label: "Kredit/Zajam" },
  { value: "hr", label: "HR / Zaposleni" },
  { value: "accessControl", label: "Pristupni profili" },
  { value: "supplier", label: "Dobavljači / Rashodi" },
  { value: "investment", label: "Investicije" },
  { value: "documents", label: "Dokumenti" },
  { value: "settings", label: "Podešavanja" },
  { value: "subscription", label: "Pretplata" },
  { value: "support", label: "Podrška" },
  { value: "patients", label: "Pacijenti" },
  { value: "venueEvent", label: "Eventi" },
];

const ACTION_OPTIONS: { value: ActivityAction; label: string }[] = [
  { value: "LOGIN", label: "Prijava" },
  { value: "CREATE", label: "Kreiranje" },
  { value: "UPDATE", label: "Izmena" },
  { value: "DELETE", label: "Brisanje" },
];

const ActivityTrail: React.FC = () => {
  const userData = useSelector((state: any) => state.userData.user);
  const authUser: any = useAuth();
  const isSuper = isSuperAdmin(userData?.roles || authUser?.roles);
  const rawClientId = userData?.clientId;
  const clientId: string = typeof rawClientId === "object" ? rawClientId?._id : rawClientId;

  const [cleanupModalOpen, setCleanupModalOpen] = useState(false);
  const [cleanupValue, setCleanupValue] = useState<number>(6);
  const [cleanupUnit, setCleanupUnit] = useState<"days" | "months">("months");
  const [previewCount, setPreviewCount] = useState<number | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [cleanupLoading, setCleanupLoading] = useState(false);

  const cleanupDays = cleanupUnit === "months" ? cleanupValue * 30 : cleanupValue;

  const handlePreviewCleanup = async () => {
    setPreviewLoading(true);
    setPreviewCount(null);
    try {
      const res = await previewActivityCleanup(cleanupDays);
      setPreviewCount(res.data.count);
    } catch {
      message.error("Greška pri provjeri broja zapisa.");
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleConfirmCleanup = async () => {
    setCleanupLoading(true);
    try {
      const res = await cleanupActivityLogs(cleanupDays);
      message.success(`Obrisano ${res.data.deletedCount} starih zapisa.`);
      setCleanupModalOpen(false);
      setPreviewCount(null);
      setPage(1);
      fetchLogs();
      fetchStats();
    } catch {
      message.error("Greška pri brisanju zapisa.");
    } finally {
      setCleanupLoading(false);
    }
  };

  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [selectedLog, setSelectedLog] = useState<ActivityLog | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const [stats, setStats] = useState<{ total: number; byModule: any[]; byAction: any[] } | null>(null);

  const [filters, setFilters] = useState<GetActivityLogsParams>({});
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null]>([null, null]);

  const fetchLogs = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const params: GetActivityLogsParams = {
        ...filters,
        page,
        limit: pageSize,
        startDate: dateRange[0]?.format("YYYY-MM-DD"),
        endDate: dateRange[1]?.format("YYYY-MM-DD"),
      };
      const res = await getActivityLogs(clientId, params);
      setLogs(res.data.logs);
      setTotal(res.data.total);
    } catch {
      //
    } finally {
      setLoading(false);
    }
  }, [clientId, filters, page, pageSize, dateRange]);

  const fetchStats = useCallback(async () => {
    if (!clientId) return;
    try {
      const res = await getActivityStats(clientId, {
        startDate: dateRange[0]?.format("YYYY-MM-DD"),
        endDate: dateRange[1]?.format("YYYY-MM-DD"),
      });
      setStats(res.data);
    } catch {
      //
    }
  }, [clientId, dateRange]);

  useEffect(() => {
    fetchLogs();
    fetchStats();
  }, [fetchLogs, fetchStats]);

  const columns: ColumnsType<ActivityLog> = [
    {
      title: "Datum i vrijeme",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 160,
      render: (v: string) => dayjs(v).format("DD.MM.YYYY HH:mm"),
      sorter: (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    },
    {
      title: "Akcija",
      dataIndex: "action",
      key: "action",
      width: 110,
      render: (v: ActivityAction) => (
        <Tag color={ACTION_COLORS[v]}>{ACTION_LABELS[v]}</Tag>
      ),
    },
    {
      title: "Modul",
      dataIndex: "module",
      key: "module",
      width: 170,
      render: (v: ActivityModule) => MODULE_LABELS[v] || v,
    },
    {
      title: "Entitet",
      dataIndex: "entityLabel",
      key: "entityLabel",
      render: (v: string) => v || "—",
    },
    {
      title: "Ko je uradio",
      dataIndex: "performedByName",
      key: "performedByName",
      width: 170,
    },
    {
      title: "Detalji",
      dataIndex: "changes",
      key: "changes",
      ellipsis: true,
      render: (v: string) => v || "—",
    },
    {
      title: "",
      key: "info",
      width: 48,
      render: (_, record) => (
        <Tooltip title="Detalji">
          <Button
            type="text"
            icon={<InfoCircleOutlined />}
            size="small"
            onClick={() => {
              setSelectedLog(record);
              setDrawerOpen(true);
            }}
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <div style={{ padding: "24px" }}>
      <Space style={{ marginBottom: 24, width: "100%", justifyContent: "space-between" }}>
        <Title level={3} style={{ margin: 0 }}>
          Aktivnosti (Audit Trail)
        </Title>
        {isSuper && (
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={() => { setPreviewCount(null); setCleanupModalOpen(true); }}
          >
            Očisti stare zapise
          </Button>
        )}
      </Space>

      {stats && (
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col>
            <Card size="small">
              <Statistic title="Ukupno akcija" value={stats.total} />
            </Card>
          </Col>
          {stats.byAction.map((a) => (
            <Col key={a._id}>
              <Card size="small">
                <Statistic
                  title={ACTION_LABELS[a._id as ActivityAction] || a._id}
                  value={a.count}
                  valueStyle={{ color: a._id === "DELETE" ? "#cf1322" : a._id === "CREATE" ? "#3f8600" : "#1677ff" }}
                />
              </Card>
            </Col>
          ))}
        </Row>
      )}

      <Card style={{ marginBottom: 16 }}>
        <Space wrap>
          <Select
            placeholder="Modul"
            allowClear
            style={{ width: 190 }}
            options={MODULE_OPTIONS}
            onChange={(v) => setFilters((f) => ({ ...f, module: v }))}
          />
          <Select
            placeholder="Akcija"
            allowClear
            style={{ width: 130 }}
            options={ACTION_OPTIONS}
            onChange={(v) => setFilters((f) => ({ ...f, action: v }))}
          />
          <RangePicker
            onChange={(vals) => setDateRange(vals ? [vals[0], vals[1]] : [null, null])}
            format="DD.MM.YYYY"
          />
          <Input
            placeholder="Pretraga (korisnik, entitet...)"
            prefix={<SearchOutlined />}
            style={{ width: 240 }}
            allowClear
            onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value || undefined }))}
          />
          <Button icon={<ReloadOutlined />} onClick={() => { setPage(1); fetchLogs(); fetchStats(); }}>
            Osvježi
          </Button>
        </Space>
      </Card>

      <Table
        columns={columns}
        dataSource={logs}
        rowKey="_id"
        loading={loading}
        pagination={{
          current: page,
          pageSize,
          total,
          onChange: setPage,
          showTotal: (t) => `Ukupno: ${t}`,
        }}
        size="small"
      />

      <Drawer
        title="Detalji aktivnosti"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={480}
      >
        {selectedLog && (
          <Space direction="vertical" style={{ width: "100%" }}>
            <div>
              <Text type="secondary">Datum i vrijeme</Text>
              <div>{dayjs(selectedLog.createdAt).format("DD.MM.YYYY HH:mm:ss")}</div>
            </div>
            <div>
              <Text type="secondary">Ko je uradio</Text>
              <div>{selectedLog.performedByName}</div>
            </div>
            <div>
              <Text type="secondary">Akcija</Text>
              <div>
                <Tag color={ACTION_COLORS[selectedLog.action]}>
                  {ACTION_LABELS[selectedLog.action]}
                </Tag>
              </div>
            </div>
            <div>
              <Text type="secondary">Modul</Text>
              <div>{MODULE_LABELS[selectedLog.module] || selectedLog.module}</div>
            </div>
            {selectedLog.entityLabel && (
              <div>
                <Text type="secondary">Entitet</Text>
                <div>{selectedLog.entityLabel}</div>
              </div>
            )}
            {selectedLog.entityId && (
              <div>
                <Text type="secondary">ID entiteta</Text>
                <div><Text code>{selectedLog.entityId}</Text></div>
              </div>
            )}
            {selectedLog.changes && (
              <div>
                <Text type="secondary">Opis</Text>
                <div>{selectedLog.changes}</div>
              </div>
            )}
            {selectedLog.ipAddress && (
              <div>
                <Text type="secondary">IP adresa</Text>
                <div>{selectedLog.ipAddress}</div>
              </div>
            )}
          </Space>
        )}
      </Drawer>

      <Modal
        title="Očisti stare zapise aktivnosti"
        open={cleanupModalOpen}
        onCancel={() => setCleanupModalOpen(false)}
        footer={null}
        width={480}
      >
        <Space direction="vertical" style={{ width: "100%" }} size="middle">
          <Text type="danger">
            Ovo trajno briše zapise aktivnosti starije od izabranog perioda — za sve klijente, ne samo za ovog. Brisanje se ne može poništiti.
          </Text>

          <Space>
            <Text>Obriši zapise starije od:</Text>
            <InputNumber
              min={1}
              value={cleanupValue}
              onChange={(v) => { setCleanupValue(v || 1); setPreviewCount(null); }}
              style={{ width: 90 }}
            />
            <Radio.Group
              value={cleanupUnit}
              onChange={(e) => { setCleanupUnit(e.target.value); setPreviewCount(null); }}
            >
              <Radio.Button value="days">dana</Radio.Button>
              <Radio.Button value="months">mjeseci</Radio.Button>
            </Radio.Group>
          </Space>

          <Button onClick={handlePreviewCleanup} loading={previewLoading}>
            Provjeri koliko će biti obrisano
          </Button>

          {previewCount !== null && (
            <>
              <Text>
                Biće obrisano <Text strong>{previewCount}</Text> zapisa (stariji od{" "}
                {dayjs().subtract(cleanupDays, "day").format("DD.MM.YYYY")}).
              </Text>
              <Button
                danger
                type="primary"
                icon={<DeleteOutlined />}
                loading={cleanupLoading}
                disabled={previewCount === 0}
                onClick={handleConfirmCleanup}
              >
                Da, trajno obriši {previewCount} zapisa
              </Button>
            </>
          )}
        </Space>
      </Modal>
    </div>
  );
};

export default ActivityTrail;
